import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

export function usePermission(permission: string): boolean {
  const { user, isAuthenticated, permissions } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return false;
    }
    return permissions.includes(permission);
  }, [isAuthenticated, user, permissions, permission]);
}

export function usePermissions(permissions: string[]): boolean[] {
  const { user, isAuthenticated, permissions: authPermissions } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return permissions.map(() => false);
    }
    return permissions.map(p => authPermissions.includes(p));
  }, [isAuthenticated, user, authPermissions, permissions]);
}

export function useAnyPermission(permissions: string[]): boolean {
  const { user, isAuthenticated, permissions: authPermissions } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return false;
    }
    return permissions.some(p => authPermissions.includes(p));
  }, [isAuthenticated, user, authPermissions, permissions]);
}

export function useAllPermissions(permissions: string[]): boolean {
  const { user, isAuthenticated, permissions: authPermissions } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return false;
    }
    return permissions.every(p => authPermissions.includes(p));
  }, [isAuthenticated, user, authPermissions, permissions]);
}

export function useRole(role: string): boolean {
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return false;
    }
    return user.role === role;
  }, [isAuthenticated, user, role]);
}

export function useAnyRole(roles: string[]): boolean {
  const { user, isAuthenticated } = useAuth();

  return useMemo(() => {
    if (!isAuthenticated || !user) {
      return false;
    }
    return roles.includes(user.role);
  }, [isAuthenticated, user, roles]);
}

export default usePermission;
